import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const BOOKING_URL = 'https://book.abbamassage.com/?aff=29';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_abba-wellness-cancun/artifacts/sr8wypsw_1.png';

export const Navbar = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [hidden, setHidden] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > 100) {
        setScrolled(true);
        if (currentScrollY > lastScrollY && currentScrollY > 200) {
          setHidden(true);
        } else {
          setHidden(false);
        }
      } else {
        setScrolled(false);
        setHidden(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const mainLinks = [
    { href: '/', label: t('nav.home') },
    { href: '/about', label: t('nav.about') },
    { href: '/contact', label: t('nav.contact') },
  ];

  const serviceLinks = [
    { href: '/in-home-massage', label: t('nav.inHome') },
    { href: '/studio-services', label: t('nav.studio') },
    { href: '/energy-work', label: t('nav.energy') },
    { href: '/events', label: t('nav.events') },
  ];

  // Spanish-only services are only listed in Spanish
  const therapyLinks = [
    ...(language === 'es' ? [{ href: '/hipnosis', label: 'Regresiones e Hipnosis' }] : []),
    { href: '/janzu', label: t('nav.janzu') },
    ...(language === 'es' ? [{ href: '/constelaciones', label: 'Constelaciones Familiares' }] : []),
  ];

  const otherLinks = [
    { href: '/hosts', label: t('nav.hosts') },
    { href: '/community', label: t('nav.community') },
    { href: '/articles', label: language === 'es' ? 'Artículos' : 'Articles' },
    { href: '/store', label: t('nav.store') },
  ];

  return (
    <motion.header
      initial={{ y: 0 }}
      animate={{ y: hidden ? -100 : 0 }}
      transition={{ duration: 0.3 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass shadow-sm' : 'bg-transparent'
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center"
            data-testid="nav-logo"
          >
            <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1.5">
              <img 
                src={LOGO_URL} 
                alt="ABBA Massage" 
                className="h-12 md:h-14 lg:h-16 w-auto object-contain"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link 
              to="/" 
              className="text-sm font-medium text-[#1C1C1C] hover:text-[#2A5C66] transition-colors"
              data-testid="nav-home"
            >
              {t('nav.home')}
            </Link>

            {/* Services Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-[#1C1C1C] hover:text-[#2A5C66] transition-colors" data-testid="nav-services-dropdown">
                {t('nav.services')}
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white/95 backdrop-blur-xl">
                {serviceLinks.map((link) => (
                  <DropdownMenuItem key={link.href} asChild>
                    <Link to={link.href} className="w-full" data-testid={`nav-${link.href.slice(1)}`}>
                      {link.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Therapies Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-[#1C1C1C] hover:text-[#2A5C66] transition-colors" data-testid="nav-therapies-dropdown">
                {t('nav.therapies')}
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white/95 backdrop-blur-xl">
                {therapyLinks.map((link) => (
                  <DropdownMenuItem key={link.href} asChild>
                    <Link to={link.href} className="w-full" data-testid={`nav-${link.href.slice(1)}`}>
                      {link.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link 
              to="/hosts" 
              className="text-sm font-medium text-[#1C1C1C] hover:text-[#2A5C66] transition-colors"
              data-testid="nav-hosts"
            >
              {t('nav.hosts')}
            </Link>

            <Link 
              to="/articles" 
              className="text-sm font-medium text-[#1C1C1C] hover:text-[#2A5C66] transition-colors"
              data-testid="nav-articles"
            >
              {language === 'es' ? 'Artículos' : 'Articles'}
            </Link>

            <Link 
              to="/about" 
              className="text-sm font-medium text-[#1C1C1C] hover:text-[#2A5C66] transition-colors"
              data-testid="nav-about"
            >
              {t('nav.about')}
            </Link>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-4">
            {/* Language Toggle with Flags */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full px-4 py-2 shadow-sm hover:shadow-md transition-all"
              data-testid="language-toggle"
            >
              {language === 'en' ? (
                <>
                  <span className="text-lg">🇲🇽</span>
                  <span className="text-sm font-semibold text-[#1C1C1C]">ES</span>
                </>
              ) : (
                <>
                  <span className="text-lg">🇺🇸</span>
                  <span className="text-sm font-semibold text-[#1C1C1C]">EN</span>
                </>
              )}
            </button>

            {/* Book Button */}
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-book-btn"
            >
              <Button className="hidden sm:flex bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-6 font-['Manrope'] font-semibold">
                {t('nav.book')}
              </Button>
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 text-[#1C1C1C]"
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-[#E5E0D6]"
            data-testid="mobile-menu"
          >
            <div className="max-w-7xl mx-auto px-6 py-6 space-y-4">
              <Link to="/" className="block py-2 text-[#1C1C1C] font-medium">{t('nav.home')}</Link>
              
              <div className="py-2">
                <p className="text-xs uppercase tracking-wider text-[#5A5A5A] mb-2">{t('nav.services')}</p>
                {serviceLinks.map((link) => (
                  <Link key={link.href} to={link.href} className="block py-2 pl-4 text-[#1C1C1C]">
                    {link.label}
                  </Link>
                ))}
              </div>

              <div className="py-2">
                <p className="text-xs uppercase tracking-wider text-[#5A5A5A] mb-2">{t('nav.therapies')}</p>
                {therapyLinks.map((link) => (
                  <Link key={link.href} to={link.href} className="block py-2 pl-4 text-[#1C1C1C]">
                    {link.label}
                  </Link>
                ))}
              </div>

              {otherLinks.map((link) => (
                <Link key={link.href} to={link.href} className="block py-2 text-[#1C1C1C] font-medium">
                  {link.label}
                </Link>
              ))}

              <Link to="/about" className="block py-2 text-[#1C1C1C] font-medium">{t('nav.about')}</Link>
              <Link to="/contact" className="block py-2 text-[#1C1C1C] font-medium">{t('nav.contact')}</Link>

              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block mt-4"
              >
                <Button className="w-full bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full font-['Manrope'] font-semibold">
                  {t('nav.book')}
                </Button>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
